import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { XMLParser } from 'fast-xml-parser';
import { ConfigService } from '../../services/config.service';
import { UserService } from '../../services/user.service';
import { formatString } from '../../utils/string-utils';

@Injectable({
	providedIn: 'root'
})
export class BrowseService {

	public config: any = {};
	pathSeparator: string = '>>';
	cleanOnLoad: boolean = false;
	manualEntryPointIndex: number = -1;
	manualEntryAllPointIndex: number = -1;

	constructor(
		private http: HttpClient,
		private configService: ConfigService,
		private userService: UserService
	) { }

	async readConfigurationFile(): Promise<any> {
		if (this.configService.getConfig().browse.enabled) {
			let context = document.location.pathname.split('/')[0];
            if (context.length == 0 && this.configService.getConfig().browse.uiApp) {
                context = this.configService.getConfig().browse.uiApp;
            }
            let url = this.configService.getConfig().browse.sourceURL+'?context=' + context;
			try {
				const browseConfig: any = await firstValueFrom(this.http.get<any>(url));
				let configFile = browseConfig.browse_config ? browseConfig.browse_config : 'config_browse.xml';
                url = this.configService.getConfig().browse.source + `/${configFile}`;
                const response = await firstValueFrom(this.http.get(url, { responseType: 'text' }));
				// Parse XML to JSON
				const parser = new XMLParser();
				const json = parser.parse(response);

				this.buildConfig(json);

				return Promise.resolve();
			} catch (error) {
				console.error("Failed to load browse configuration file.");
				return Promise.reject(error);
			}
		}
	}

	private buildConfig(json: any): void {
		let globals = json.settings.globals;
		this.config.treeMode = (globals.browseTreeMode && 'true' == globals.browseTreeMode.toLowerCase()) ? true : false
		this.config.canEdit = (globals.canEdit && 'true' == globals.canEdit.toLowerCase() && this.userService.user.loggedin) ? true : false
		this.config.maxFetched = (!globals.maxFetched) ? -1 : (globals.maxFetched && ('all' == globals.maxFetched.toLowerCase() || globals.maxFetched < 1)) ? -1 : globals.maxFetched
		this.config.pageSize = (globals.pageSize && globals.pageSize > 0) ? globals.pageSize * 1 : 15
		this.config.pagination = (globals.pagination && 'true' == globals.pagination.toLowerCase()) ? true : false
		this.config.showRecordsFilter = globals.showRecordsFilter;

		this.config.entryPoints = [];
		this.config.allEntryPoints = json.settings.browseEntryPoints.browseEntryPoint || [];
		if (!(this.config.allEntryPoints instanceof Array)) {
			this.config.allEntryPoints = [this.config.allEntryPoints];
		}
		for (let i = 0; i < this.config.allEntryPoints.length; i++) {
			let entryPoint = this.config.allEntryPoints[i];
			entryPoint.includeEmptyFolders = (entryPoint.includeEmptyFolders && 'true' == entryPoint.includeEmptyFolders.toLowerCase()) ? true : false
			entryPoint.showDirectChildsOnly = (entryPoint.showDirectChildsOnly && 'true' == entryPoint.showDirectChildsOnly.toLowerCase()) ? true : false
			entryPoint.structures = entryPoint.structure.split("||");

			entryPoint.plainstructures = [];
			for (let j = 0; j < entryPoint.structures.length; j++) {
				let current = entryPoint.structures[j];
				let idx = current.lastIndexOf("_ss");
				let field = idx > 0 ? current.substring(0, idx) : current
				entryPoint.plainstructures.push(field);
			}
			entryPoint.allowedUserGroups = entryPoint.allowedUserGroups.split(/[\s,]+/);
			// Only restrict access if there is an explicit whitelist defined
			if (entryPoint.allowedUserGroups.length == 1 && entryPoint.allowedUserGroups[0] == "") { entryPoint.allowedUserGroups[0] = "ALL" }
			for (let j = 0; j < entryPoint.allowedUserGroups.length; j++) {
				if (this.userService.user.group == entryPoint.allowedUserGroups[j] || "ALL" == entryPoint.allowedUserGroups[j].toUpperCase()) {
					this.config.entryPoints.push(entryPoint);
					break;
				}
			}
		}
	}

	manualBrowseConfiguration(config: any): void {
		// We handle the dashboard use case here
		this.config.treeMode = config.treeMode || false;
		this.config.canEdit = config.canEdit || false;
		this.config.maxFetched = config.maxFetched || this.config.maxFetched || -1;
		this.config.pageSize = this.config.pageSize || 15;
		this.config.pagination = this.config.pagination || true;
		this.config.showRecordsFilter = config.showRecordsFilter || "";
		this.config.showDirectChildsOnly = config.showDirectChildsOnly === true ||
					  (typeof config.showDirectChildsOnly === 'string' &&
					    config.showDirectChildsOnly.toLowerCase() === 'true'); 
		this.config.actionSource = (config.action && "browsebox" == config.action.toLowerCase()) ? "dashboard" : "user";

		this.config.entryPoints = this.config.entryPoints || [];
		let entryPoint = config;
		if (typeof entryPoint.includeEmptyFolders === 'string' || entryPoint.includeEmptyFolders instanceof String) {
			entryPoint.includeEmptyFolders = 'true' == entryPoint.includeEmptyFolders.toLowerCase() ? true : false;
		}
		if (typeof entryPoint.showDirectChildsOnly === 'string' || entryPoint.showDirectChildsOnly instanceof String) {
			entryPoint.showDirectChildsOnly = 'true' == entryPoint.showDirectChildsOnly.toLowerCase() ? true : false;
		}
		entryPoint.structures = entryPoint.structure.split("||");
		entryPoint.plainstructures = [];
		for (let j = 0; j < entryPoint.structures.length; j++) {
			let current = entryPoint.structures[j];
			let idx = current.lastIndexOf("_ss");
			let field = idx > 0 ? current.substring(0, idx) : current;
			entryPoint.plainstructures.push(field);
		}
		entryPoint.allowedUserGroups = ["ALL"];
		entryPoint.label = entryPoint.browseLabel || config.label || this.userService.LOCALIZATION.allDocuments;
		entryPoint.description = config.description || "";

		let criteria = entryPoint.dataQuery.split(",");
		let parsedCriteria = [];
		for (let i = 0; i < criteria.length; i++) {
			if (criteria[i]) {
				let path = criteria[i].replace("=", ":").replace("[", "").replace("]", "");
				parsedCriteria.push(path);
			}
		}
		entryPoint.path = parsedCriteria.join("||");

		this.config.entryPoints.push(entryPoint);
		this.manualEntryPointIndex = this.config.entryPoints.length - 1;
		this.config.allEntryPoints = this.config.allEntryPoints || [];
		this.config.allEntryPoints.push(entryPoint);
		this.manualEntryAllPointIndex = this.config.allEntryPoints.length - 1;
	}

	resetentryPoints(){
		if(this.manualEntryPointIndex >-1 && this.config.entryPoints && 
		this.config.entryPoints.length>this.manualEntryPointIndex){
			this.config.entryPoints.splice(this.manualEntryPointIndex, 1);
			this.manualEntryPointIndex = -1;				
		}
		if(this.manualEntryAllPointIndex >-1 && this.config.allEntryPoints && 
		this.config.allEntryPoints.length>this.manualEntryAllPointIndex){
			this.config.allEntryPoints.splice(this.manualEntryAllPointIndex, 1);
			this.manualEntryAllPointIndex = -1;				
		}
	}
	
	async getBrowseNode(path: string, options: any): Promise<any> {
		if (this.configService.getConfig().browse.enabled) {

			let url = formatString(this.configService.getConfig().browse.browseURL, this._URLifyPath(path));
			if (!url.endsWith("/")) {
				url = url + "/";
			}

			// Convert 'options' to URL parameters (replaces '$.param(options)')
			if (options["structure"]) {
				options["structure"] = options["structure"].replaceAll('>>',',')
			}
			const params = new HttpParams({ fromObject: options });

			const headers = new HttpHeaders({
				'Authorization': 'none'
			});

			try {
				const response = await firstValueFrom(this.http.get<any>(url, { params, headers }));

				response.result = [];
				let values = response.values;
				if (values) {
					for (let i = 0; i < values.length; i++) {
						let node = values[i];
						node.displayvalue = this.getNodeDisplayLabel(node.value);
						node.plainname = this.getPlainNodeName(node.name);
						node.plainvalue = node.label;
						node.browsepath = this.getBrowsePath(path, node);
						node.searchpath = this.getSearchPath(path, node);
						delete node.label;
					}
					response.result = response.values;
					delete response.values;
				}

				return response;
			} catch (error) {
				return Promise.reject(error);
			}
		}
	}

	async getBrowsePathsForProducts(prodids: string[]): Promise<any> {
		let url = this.configService.getConfig().browse.pathsURL;

		try {
			return await firstValueFrom(this.http.post<any>(url, prodids));
		} catch (error) {
			return Promise.reject(error);
		}
	}

	private getNodeDisplayLabel(foldername: string): string {
		let displayval = foldername || "";
		let pos = displayval.lastIndexOf('[');
		let bracket = (pos > -1) ? displayval.substring(pos) : ""
		if (pos > 30 || (pos == -1 && displayval.length > 30)) {
			displayval = displayval.substring(0, 30) + '.. ' + bracket;
		} else if (pos > -1) {
			displayval = displayval.substring(0, pos) + " " + bracket;
		}
		return displayval;
	}

	private getPlainNodeName(name: string): string {
		let idx = name.lastIndexOf("_ss");
		let plain = idx > 0 ? name.substring(0, idx) : name
		return plain;
	}

	private getBrowsePath(searchpath: string, node: any): string {
		let root = searchpath.replace(/_ss$/, '');
		// Filter out any falsy values before joining
		let path =
			[root, node.plainname + ':' + node.plainvalue].filter(Boolean).join(this.pathSeparator)
		return path;
	}

	private getSearchPath(searchpath: string, node: any): string {
		let root = searchpath;
		// Filter out any falsy values before joining
		let path =
			[root, node.name + ':' + node.plainvalue].filter(Boolean).join(this.pathSeparator)
		return path;
	}

	private _URLifyPath(path: string): string {
		path = path || "";

		// See org.springframework.security.web.firewall.StrictHttpFirewall for a list of rejected URL characters deemed malicious:
		//        	Rejects URLs that contain semicolons.
		//        	Rejects URLs that contain a URL encoded slash.
		//        	Rejects URLs that contain a backslash.
		//        	Rejects URLs that contain a null character.
		//        	Rejects URLs that contain a URL encoded percent.

		path = path.replace(/\;/g, '###');
		path = path.replace(/\//g, '___');
		path = path.replace(/\%/g, '-_-');
		let formatted = encodeURIComponent(path);
		return formatted;
	}
}
